<template>
  <div class="predictive-search">
    <div class="predictive-search__inner">
      <div class="predictive-search__header">
        <button
          class="predictive-search__close icon-button"
          type="button"
          @click.prevent="handleClose"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.close')"
          />

          <icon-close />
        </button>

        <div>
          <h2
            id="overlay-predictiveSearch"
            class="text-heading-2xl mb-m"
            :class="{ [$class('visuallyHidden')]: !isMobile }"
            v-text="$string('predictive_search.search')"
          />

          <search-bar
            v-if="isMobile"
            ref="searchBar"
            class="predictive-search__search-bar"
            :namespace="`predictive-${namespace}`"
            redirect
            :search-query="searchQuery"
            @search-input="(input) => searchInput(input)"
            @clear="clear"
          />
        </div>
      </div>

      <div
        v-if="searchQuery && !noResults"
        class="predictive-search__body"
      >
        <predictive-search-results
          :articles="searchResults.articles"
          :collections="searchResults.collections"
          :pages="searchResults.pages"
          :products="searchResults.products"
          :suggestions="searchResults.queries"
          :search-query="searchQuery"
        />
      </div>

      <div
        v-else
        class="predictive-search__empty"
      >
        <predictive-search-empty
          :search-query="searchQuery"
        />
      </div>

      <transition name="fade">
        <div
          v-if="searchQuery"
          class="predictive-search__footer"
        >
          <btn
            class="predictive-search__view-all"
            :label="
              $string(
                'predictive_search.view_all',
                { replace: { term: encodeURIComponent(searchQuery) } },
              )
            "
            :url="`${$variable('routes.search')}?q=${encodeURIComponent(searchQuery)}&type=product`"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Overlay to display predictive search results.
 *
 * @param {Array} headerSearchBar - Search bar component in site header.
 * @param {String} namespace - Unique identifier.
 * @param {Object} overlay - Overlay API.
 * @param {String} searchQuery - Current search query.
 *
 * @emits searchInput - Emit new search query.
 * @emits clear - Emit clear search input.
 *
 */
import { mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import PredictiveSearchEmpty from '~async/predictive-search/predictive-search-empty'
import PredictiveSearchResults from '~async/predictive-search/predictive-search-results'
import SearchBar from '~global/search-bar/search-bar'

import IconClose from '~icons/directional-navigation/close.svg'

import predictiveSearchQuery from '~graphql/predictive-search.gql'

import { formatPredictiveSearchResults } from '~/format/format-predictive-search'
import { focusable } from '~/helpers/accessibility'
import { buildTaxonomyMetafieldIdentifiers } from '~/helpers/garage'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'PredictiveSearch',

  components: {
    Btn,
    IconClose,
    PredictiveSearchEmpty,
    PredictiveSearchResults,
    SearchBar,
  },

  props: {
    headerSearchBar: {
      type: Object,
      default: () => ({}),
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  emits: {
    'search-input': (payload) => typeof payload === 'string',
    clear: () => true,
  },

  data() {
    return {
      state: 'loading',
      searchResults: {
        articles: [],
        collections: [],
        pages: [],
        products: [],
        queries: [],
      },
      searchVariables: {
        country: cnvs.store.country,
        language: cnvs.store.language,
        query: '',
        types: [
          'ARTICLE',
          'COLLECTION',
          'PAGE',
          'PRODUCT',
          'QUERY',
        ],
        searchableFields: [
          'TITLE',
          'PRODUCT_TYPE',
          'VARIANTS_TITLE',
          'VENDOR',
          'BODY',
        ],
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.s}) 33vw, 30vw`
    },

    /**
     * Returns true if no reuslts are found.
     */
    noResults() {
      return !this.searchResults.articles.length &&
        !this.searchResults.collections.length &&
        !this.searchResults.pages.length &&
        !this.searchResults.products.length &&
        !this.searchResults.queries.length
    },

    /**
     * Compute product taxonomy metafield identifiers.
     * @returns {Array}
     */
    taxonomyMetafieldIdentifiers() {
      return buildTaxonomyMetafieldIdentifiers()
    },
  },

  watch: {

    /**
     * Watch for changes in entered search query.
     */
    searchQuery() {
      if (!this.searchQuery) {
        this.overlay.update({
          currentElement: document.activeElement,
          focusElements: {
            elements: focusable(this.headerSearchBar),
          },
        })
        return
      }

      this.searchVariables.query = this.searchQuery
      this.runSearchQuery()
    },

    /**
     * Watch active overlay to focus search bar.
     */
    async getActiveOverlay() {
      if (
        this.getActiveOverlay?.namespace !== 'predictiveSearch' ||
        !this.isMobile
      ) {
        return
      }

      // Focus search bar input after overlay opened.
      await this.$nextTick()
      setTimeout(() => {
        this.$refs.searchBar.$refs.input.focus()
      }, this.$timing('slow'))
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    this.overlay.update({
      currentElement: document.activeElement,
      focusElements: {
        elements: focusable(this.headerSearchBar),
      },
    })

    if (!this.searchQuery) {
      return
    }

    this.searchVariables.query = this.searchQuery
    this.runSearchQuery()
  },

  methods: {

    /**
     * Run search query and sets the results
     */
    async runSearchQuery() {
      try {
        const response = await cnvs.Query({
          query: predictiveSearchQuery,
          variables: {
            ...this.searchVariables,
            taxonomyMetafieldIdentifiers: this.taxonomyMetafieldIdentifiers,
          },
        })

        this.searchResults = formatPredictiveSearchResults(response)

      } catch (error) {
        cnvs.ReportError('Failed to perform search', error)
      }
    },

    /**
     * Emits search input changes.
     * @param {String} value - search input.
     */
    searchInput(value) {
      this.$emit('search-input', value)
    },

    /**
     * Clear search query by emiting event and changing search state.
     */
    clear() {
      this.$emit('clear')
    },

    /**
     * Close predictive search overlay.
     */
    handleClose() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './predictive-search';
@import './predictive-search-empty';
@import './predictive-search-results';
</style>
